import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace(/^http/, 'ws');

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};

// Request push notification permission
async function requestPermission() {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
}

// Send push notification via service worker
function sendPushNotification(data) {
  if (Notification.permission !== 'granted') return;
  if (!navigator.serviceWorker?.controller) return;

  navigator.serviceWorker.controller.postMessage({
    type: 'SHOW_NOTIFICATION',
    payload: {
      title: data.title,
      body: data.message,
      icon: '/icon-192.png',
      tag: data.notification_id || 'kstreak',
      link: data.link || '/dashboard'
    }
  });
}

export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushPermission, setPushPermission] = useState('default');
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  // Request push permission once user is logged in
  useEffect(() => {
    if (user && 'Notification' in window) {
      setPushPermission(Notification.permission);
      if (Notification.permission === 'default') {
        // Delay prompt to avoid blocking first load
        const timer = setTimeout(() => {
          requestPermission().then(setPushPermission);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  // Listen for SW navigation messages (when user clicks a push notification)
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'NAVIGATE' && event.data.link) {
        window.location.href = event.data.link;
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications?limit=30`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }),
        axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        })
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch (err) {
      console.debug('Failed to fetch notifications:', err);
    }
  }, [token]);

  const connectWebSocket = useCallback(() => {
    if (!token || !WS_URL) return;
    // Close existing
    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket(`${WS_URL}/ws/notifications/${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.debug('WS connected');
      // Keep alive with pings every 30s
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 30000);
      ws._pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const payload = JSON.parse(event.data);
        if (payload.event === 'notification') {
          setNotifications(prev => [payload.data, ...prev]);
          setUnreadCount(prev => prev + 1);

          // Show OS push notification when tab is inactive/hidden
          if (document.hidden || !document.hasFocus()) {
            sendPushNotification(payload.data);
          }
        }
      } catch (e) {
        // ignore
      }
    };

    ws.onclose = () => {
      if (ws._pingInterval) clearInterval(ws._pingInterval);
      // Reconnect after 5s
      reconnectTimer.current = setTimeout(() => {
        if (token) connectWebSocket();
      }, 5000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token]);

  useEffect(() => {
    if (user && token) {
      fetchNotifications();
      connectWebSocket();
    }
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [user, token, fetchNotifications, connectWebSocket]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.debug('Failed to mark notification read:', err);
    }
  }, [token]);

  const markAllAsRead = useCallback(async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.debug('Failed to mark all read:', err);
    }
  }, [token]);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setNotifications(prev => {
        const notif = prev.find(n => n.notification_id === notificationId);
        if (notif && !notif.is_read) setUnreadCount(c => Math.max(0, c - 1));
        return prev.filter(n => n.notification_id !== notificationId);
      });
    } catch (err) {
      console.debug('Failed to delete notification:', err);
    }
  }, [token]);

  const requestPushPermission = useCallback(async () => {
    const result = await requestPermission();
    setPushPermission(result);
    return result;
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      pushPermission,
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      requestPushPermission
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
